import React, { useMemo, useState } from 'react';
import { View, Text, TextInput, Image, StyleSheet, Pressable } from 'react-native';
import MaterialCommunityIcons from '@expo/vector-icons/MaterialCommunityIcons';
import { useDropzone } from 'react-dropzone';
import DataOps from '../../../../data/DataOps';
import AccountTypeBadge from '../../../../components/general/AccountTypeBadge';

interface ProfileHeaderProps {
  user: any;
  isEditing: boolean;
  profileName: string;
  onEditName: () => void;
  getAccountDescription: () => string;
  setUser: (u: any) => void;
}

const ProfileHeader: React.FC<ProfileHeaderProps> = ({
  user,
  isEditing,
  profileName,
  onEditName,
  getAccountDescription,
  setUser,
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const getInitials = useMemo(() => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(0)}`.toUpperCase();
    }
    return 'U';
  }, [user?.firstName, user?.lastName]);

  const onDrop = async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0 || !user) return;
    const file = acceptedFiles[0];
    try {
      const reader = new FileReader();
      reader.onload = async (e) => {
        const imageDataUrl = e.target?.result as string;
        const docId = user.userId || user.id;
        if (!docId) {
          console.error('Missing user id for profile image save');
          return;
        }

        const uploadedImageUrl = await DataOps.uploadImage(
          imageDataUrl,
          `profile_${docId}_${Date.now()}`
        );

        if (!uploadedImageUrl) {
          console.error('Failed to upload profile image to storage');
          return;
        }

        const updatedUser = {
          ...user,
          profileImage: uploadedImageUrl,
          firstName: user.firstName || '',
          lastName: user.lastName || '',
          userName: user.userName || '',
          phone: user.phone || '',
          address: {
            street: user.address?.street || '',
            city: user.address?.city || '',
            state: user.address?.state || '',
            zipCode: user.address?.zipCode || '',
            country: user.address?.country || '',
          },
          accountType: user.accountType || 'user',
          children: user.children || [],
          likedCamps: user.likedCamps || [],
          likedSessions: user.likedSessions || [],
          camps: user.camps || [],
          paymentMethods: user.paymentMethods || [],
        };
        const collectionName = DataOps.getCollectionNameForAccountType(user.accountType);
        const saveSuccess = await DataOps.saveCustomDataToFirebase(collectionName, docId, updatedUser, true);

        if (!saveSuccess) {
          console.error('Failed to persist profile image to backend');
          return;
        }
        await DataOps.saveDataToStorage('user', updatedUser);
        setUser(updatedUser);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      console.error('Error uploading profile image:', err);
    }
  };

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: { 'image/*': ['.jpeg', '.jpg', '.png', '.gif'] },
    maxFiles: 1,
  });

  return (
    <View style={localStyles.profileContainer}>
      <View style={localStyles.headerBackground} />

      <View style={localStyles.content}>
        <View style={localStyles.avatarWrapper}>
          <Pressable
            onHoverIn={() => setIsHovered(true)}
            onHoverOut={() => setIsHovered(false)}
            style={{ borderRadius: 65 }} // Match avatarInner radius
          >
            {isEditing ? (
              <div {...getRootProps()} style={localStyles.avatarInner as any}>
                <input {...getInputProps()} />
                {user?.profileImage ? (
                  <Image source={{ uri: user.profileImage }} style={localStyles.avatarImage} />
                ) : (
                  <View style={[localStyles.avatarPlaceholder, { backgroundColor: '#3C8248' }]}>
                    <Text style={localStyles.avatarText}>{getInitials}</Text>
                  </View>
                )}
                {isHovered && (
                  <View style={localStyles.glassOverlay}>
                    <MaterialCommunityIcons name="camera-plus" size={40} color="white" />
                    <Text style={localStyles.uploadText}>Update Photo</Text>
                  </View>
                )}
              </div>
            ) : (
              <View style={localStyles.avatarInner}>
                {user?.profileImage ? (
                  <Image source={{ uri: user.profileImage }} style={localStyles.avatarImage} />
                ) : (
                  <View style={[localStyles.avatarPlaceholder, { backgroundColor: '#3C8248' }]}>
                    <Text style={localStyles.avatarText}>{getInitials}</Text>
                  </View>
                )}
              </View>
            )}
          </Pressable>
          {!isEditing && (
            <View style={localStyles.statusBadge}>
              <View style={localStyles.statusDot} />
            </View>
          )}
        </View>

        <View style={localStyles.infoContainer}>
          <Pressable onPress={onEditName} style={localStyles.profileNameContainer}>
            <Text style={localStyles.profileNameText}>{profileName || 'My profile'}</Text>
            <MaterialCommunityIcons name="pencil" size={16} color="#94A3B8" style={localStyles.nameEditIcon} />
          </Pressable>

          <Text style={localStyles.description}>
            {getAccountDescription()}
          </Text>
          <View style={localStyles.accountTypeBadgeWrap}>
            <AccountTypeBadge accountType={user?.accountType} compact />
          </View>

          {!isEditing && (
            <View style={localStyles.badgeContainer}>
              <View style={localStyles.memberBadge}>
                <MaterialCommunityIcons name="shield-check" size={16} color="#3C8248" />
                <Text style={localStyles.memberBadgeText}>
                  Member since {user?.dateJoined
                    ? (() => {
                      try {
                        if (user.dateJoined?.toDate) {
                          const date = user.dateJoined.toDate();
                          return date.getFullYear();
                        }
                        const dateStr = String(user.dateJoined).trim().replace(/['"]/g, '');
                        const date = new Date(dateStr);
                        return isNaN(date.getTime()) ? '2024' : date.getFullYear();
                      } catch (e) {
                        return '2024';
                      }
                    })()
                    : '2024'}
                </Text>
              </View>
              <Pressable style={localStyles.learnMoreLink}>
                <Text style={localStyles.learnMoreText}>Profile Settings</Text>
                <MaterialCommunityIcons name="chevron-right" size={14} color="#64748B" />
              </Pressable>
            </View>
          )}
        </View>
      </View>
    </View>
  );
};

export default ProfileHeader;

const localStyles = StyleSheet.create({
  profileContainer: {
    backgroundColor: '#FFFFFF',
    borderRadius: 32,
    borderWidth: 1,
    borderColor: '#F1F5F9',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 10 },
    shadowOpacity: 0.05,
    shadowRadius: 20,
    elevation: 5,
    marginBottom: 24,
    overflow: 'hidden',
  },
  headerBackground: {
    height: 100,
    backgroundColor: '#FFFFFF',
    borderBottomWidth: 1,
    borderBottomColor: '#F8FAFC',
  },
  content: {
    alignItems: 'center',
    marginTop: -55,
    paddingBottom: 32,
    paddingHorizontal: 24,
  },
  avatarWrapper: {
    position: 'relative',
    marginBottom: 20,
  },
  avatarInner: {
    width: 130,
    height: 130,
    borderRadius: 65,
    borderWidth: 6,
    borderColor: '#FFFFFF',
    backgroundColor: '#F1F5F9',
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
    position: 'relative',
  },
  avatarImage: {
    width: '100%',
    height: '100%',
  },
  avatarPlaceholder: {
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
  },
  avatarText: {
    color: '#fff',
    fontSize: 48,
    fontWeight: '800',
  },
  glassOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.4)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  uploadText: {
    color: 'white',
    fontSize: 12,
    fontWeight: '700',
    marginTop: 4,
  },
  statusBadge: {
    position: 'absolute',
    bottom: 8,
    right: 8,
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
    alignItems: 'center',
    justifyContent: 'center',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 4,
    elevation: 2,
    zIndex: 10,
  },
  statusDot: {
    width: 14,
    height: 14,
    borderRadius: 7,
    backgroundColor: '#3C8248',
    borderWidth: 2,
    borderColor: '#FFFFFF',
  },
  infoContainer: {
    alignItems: 'center',
    width: '100%',
  },
  profileNameContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    marginBottom: 6,
  },
  nameEditIcon: {
    opacity: 0.6,
  },
  profileNameText: {
    fontSize: 28,
    fontWeight: '800',
    color: '#1E293B',
    textAlign: 'center',
    letterSpacing: -0.5,
  },
  description: {
    fontSize: 15,
    color: '#64748B',
    textAlign: 'center',
    marginBottom: 10,
    lineHeight: 22,
    maxWidth: 400,
  },
  accountTypeBadgeWrap: {
    marginBottom: 18,
  },
  badgeContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  memberBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F0FDF4',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#DCFCE7',
    gap: 6,
  },
  memberBadgeText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#166534',
  },
  learnMoreLink: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 6,
    paddingHorizontal: 8,
  },
  learnMoreText: {
    fontSize: 13,
    fontWeight: '600',
    color: '#64748B',
  },
});


